'use client';

import {
  arrow,
  autoUpdate,
  FloatingArrow,
  FloatingFocusManager,
  FloatingPortal,
  flip,
  offset,
  Placement,
  shift,
  size,
  UseClickProps,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import {focusManager} from '@tanstack/react-query';
import {AnimatePresence, motion} from 'motion/react';
import {
  cloneElement,
  forwardRef,
  ReactElement,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import {getMotionProps} from '../../libs';
import {PopperContext} from './PopperContext';

interface PopperProps {
  children: ReactElement<{
    onClick?: () => void;
  }>;
  menu: ReactElement;
  placement?: Placement;
  width?: 'inherit' | 'auto';
  hasArrow?: boolean;
  click?: UseClickProps;
}

export interface PopperHandle {
  update: () => void;
}

const menuMotionProps = getMotionProps({
  initial: {opacity: 0, y: -4},
  animate: {opacity: 1, y: 0},
  exit: {opacity: 0, y: -4},
});

const FLOATING_MAX_WIDTH = 256;

export const Popper = forwardRef<PopperHandle, PopperProps>(
  ({children, menu, placement = 'bottom-start', width = 'inherit', hasArrow, click}, ref) => {
    const arrowRef = useRef(null);
    const [open, setOpen] = useState(false);

    const {refs, floatingStyles, context} = useFloating({
      open,
      placement,
      strategy: 'fixed',
      middleware: [
        shift({crossAxis: false, padding: hasArrow ? 5 + 8 : 5}),
        flip({mainAxis: true}),
        hasArrow && arrow({element: arrowRef}),
        offset(hasArrow ? 5 + 8 : 5),
        size(() => ({
          apply({rects, elements}) {
            Object.assign(
              elements.floating.style,
              width === 'inherit'
                ? {
                    minWidth: `${Math.min(rects.reference.width, FLOATING_MAX_WIDTH)}px`,
                    maxWidth: `${FLOATING_MAX_WIDTH}px`,
                  }
                : {},
            );
          },
        })),
      ],
      whileElementsMounted: autoUpdate,
      onOpenChange: setOpen,
    });

    useImperativeHandle(
      ref,
      () => ({
        update: context.update,
      }),
      [context.update],
    );

    const {getReferenceProps, getFloatingProps} = useInteractions([
      useClick(context, {toggle: true, ...click}),
      useDismiss(context),
    ]);

    const onClick = useCallback(() => {
      if (click?.enabled === false) {
        return;
      }

      if (click?.toggle !== false) {
        setOpen((o) => !o);
      } else {
        setOpen(true);
      }
      children.props.onClick?.();
    }, [children.props.onClick, click]);

    const referenceProps = {
      ...getReferenceProps({ref: refs.setReference}),
      'data-floating-open': open,
      onClick,
    };

    const floatingProps = {
      ...getFloatingProps({ref: refs.setFloating}),
      style: floatingStyles,
    };

    return (
      <PopperContext.Provider value={{open, setOpen}}>
        {cloneElement(children, referenceProps)}
        <AnimatePresence>
          {open && (
            <FloatingFocusManager {...focusManager} context={context} initialFocus={0}>
              <FloatingPortal>
                <div {...floatingProps} className="z-5000">
                  <motion.div {...menuMotionProps}>
                    {hasArrow && (
                      <FloatingArrow
                        ref={arrowRef}
                        context={context}
                        fill="var(--color-bg-surface)"
                        width={12}
                        tipRadius={2}
                      />
                    )}

                    {menu}
                  </motion.div>
                </div>
              </FloatingPortal>
            </FloatingFocusManager>
          )}
        </AnimatePresence>
      </PopperContext.Provider>
    );
  },
);
